import React, { useState, useEffect } from 'react';
import {
  Calculator,
  Plus,
  Trash2,
  FileSpreadsheet,
  CheckCircle2,
  Percent,
  TrendingUp,
  Sparkles,
  ArrowRight,
  RefreshCw,
  Sliders,
  DollarSign,
  Layers,
  FileText,
} from 'lucide-react';
import { EstimatorTemplateItem, EstimatorLineTemplate } from '../types/index.ts';
import { useAuth } from '../context/AuthContext.tsx';

interface EstimatorTemplatePageProps {
  onNavigate: (route: string, param?: string) => void;
}

export const EstimatorTemplatePage: React.FC<EstimatorTemplatePageProps> = ({ onNavigate }) => {
  const { currentRole, currentUser } = useAuth();
  const isManagerOrAdmin = currentRole === 'ADMIN' || currentRole === 'MANAGEMENT';

  const [templates, setTemplates] = useState<EstimatorTemplateItem[]>([]);
  const [selectedTemplateCode, setSelectedTemplateCode] = useState<string>('AC');
  const [loading, setLoading] = useState(true);

  // Line items state
  const [lineItems, setLineItems] = useState<EstimatorLineTemplate[]>([]);

  // Sliders
  const [wastagePercent, setWastagePercent] = useState<number>(5);
  const [overheadPercent, setOverheadPercent] = useState<number>(10);
  const [markupPercent, setMarkupPercent] = useState<number>(20);
  const [taxPercent, setTaxPercent] = useState<number>(13);

  // Calculation output
  const [calculation, setCalculation] = useState<{
    materialSubtotal: number;
    labourSubtotal: number;
    equipmentSubtotal: number;
    transportSubtotal: number;
    directCost: number;
    wastageCost: number;
    overheadCost: number;
    markupCost: number;
    subtotalBeforeTax: number;
    taxCost: number;
    grandTotal: number;
  }>({
    materialSubtotal: 0,
    labourSubtotal: 0,
    equipmentSubtotal: 0,
    transportSubtotal: 0,
    directCost: 0,
    wastageCost: 0,
    overheadCost: 0,
    markupCost: 0,
    subtotalBeforeTax: 0,
    taxCost: 0,
    grandTotal: 0,
  });

  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [isPushingBOQ, setIsPushingBOQ] = useState(false);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/estimator/templates');
      if (res.ok) {
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          setTemplates(data);
          loadTemplate(data[0]);
        }
      }
    } catch (err: any) {
      console.warn('Estimator load notice:', err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const loadTemplate = (tpl: EstimatorTemplateItem) => {
    setSelectedTemplateCode(tpl.workTypeCode);
    setWastagePercent(Number(tpl.defaultWastagePercent) || 5);
    setOverheadPercent(Number(tpl.defaultOverheadPercent) || 10);
    setMarkupPercent(Number(tpl.defaultMarkupPercent) || 20);
    setTaxPercent(Number(tpl.defaultTaxPercent) || 13);

    try {
      const parsed = typeof tpl.itemsJson === 'string' ? JSON.parse(tpl.itemsJson) : tpl.itemsJson;
      setLineItems(Array.isArray(parsed) ? parsed : []);
    } catch (e) {
      setLineItems([]);
    }
  };

  // Recalculate whenever items or sliders change
  useEffect(() => {
    let mat = 0;
    let lab = 0;
    let eq = 0;
    let trans = 0;

    lineItems.forEach((item) => {
      const q = Number(item.defaultQty) || 0;
      mat += q * (Number(item.materialRate) || 0);
      lab += q * (Number(item.labourRate) || 0);
      eq += q * (Number(item.equipmentRate) || 0);
      trans += q * (Number(item.transportRate) || 0);
    });

    const direct = mat + lab + eq + trans;
    const wastage = mat * (wastagePercent / 100);
    const costWithWastage = direct + wastage;
    const overhead = costWithWastage * (overheadPercent / 100);
    const costWithOverhead = costWithWastage + overhead;
    const markup = costWithOverhead * (markupPercent / 100);
    const subtotal = costWithOverhead + markup;
    const tax = subtotal * (taxPercent / 100);
    const grand = subtotal + tax;

    setCalculation({
      materialSubtotal: mat,
      labourSubtotal: lab,
      equipmentSubtotal: eq,
      transportSubtotal: trans,
      directCost: direct,
      wastageCost: wastage,
      overheadCost: overhead,
      markupCost: markup,
      subtotalBeforeTax: subtotal,
      taxCost: tax,
      grandTotal: grand,
    });
  }, [lineItems, wastagePercent, overheadPercent, markupPercent, taxPercent]);

  const handleUpdateItem = (index: number, field: keyof EstimatorLineTemplate, value: any) => {
    setLineItems((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const handleAddItem = () => {
    const newItem: EstimatorLineTemplate = {
      itemNumber: lineItems.length + 1,
      description: 'Additional custom line item or material',
      unit: 'Job',
      defaultQty: 1,
      materialRate: 1500,
      labourRate: 800,
      equipmentRate: 0,
      transportRate: 200,
    };
    setLineItems([...lineItems, newItem]);
  };

  const handleDeleteItem = (index: number) => {
    setLineItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handlePushToBOQ = async () => {
    try {
      setIsPushingBOQ(true);
      const activeTemplate = templates.find((t) => t.workTypeCode === selectedTemplateCode);
      const boqData = {
        branchId: 1,
        title: `BOQ: ${activeTemplate?.workTypeName || selectedTemplateCode}`,
        category: 'HERE4U Standard Maintenance',
        totalAmount: calculation.grandTotal,
        notes: `Generated via HERE4U Estimator Engine. Wastage: ${wastagePercent}%, Markup: ${markupPercent}%, Tax: ${taxPercent}%.`,
        items: lineItems.map((li) => ({
          itemNumber: String(li.itemNumber),
          description: li.description,
          unit: li.unit,
          quantity: li.defaultQty,
          unitRate: (Number(li.materialRate) + Number(li.labourRate)),
          totalAmount: li.defaultQty * (Number(li.materialRate) + Number(li.labourRate)),
        })),
      };

      const res = await fetch('/api/boqs', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(boqData),
      });

      if (res.ok) {
        setToastMessage('Estimate successfully converted into official ERP BOQ!');
        setTimeout(() => onNavigate('boqs'), 1200);
      } else {
        setToastMessage('Failed to push estimate to BOQ');
      }
    } catch (err: any) {
      setToastMessage(err.message || 'Error generating BOQ');
    } finally {
      setIsPushingBOQ(false);
      setTimeout(() => setToastMessage(null), 4000);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-2 border-b border-slate-200">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
              HERE4U Estimator Template Engine
            </h1>
            <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-emerald-100 text-emerald-900 border border-emerald-300">
              9 Work Types
            </span>
          </div>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Dynamic cost estimation with automated wastage factoring (5–10%), equipment, transport, contractor markup, and direct BOQ/quotation generation.
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button
            onClick={() => onNavigate('boqs')}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm transition-colors"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 text-blue-600" />
            Existing BOQs
          </button>

          <button
            onClick={handlePushToBOQ}
            disabled={isPushingBOQ}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold rounded-lg bg-blue-700 text-white hover:bg-blue-800 shadow-sm transition-colors disabled:opacity-50"
          >
            <Sparkles className="w-3.5 h-3.5 text-blue-200" />
            {isPushingBOQ ? 'Generating BOQ...' : 'Convert to BOQ'}
          </button>
        </div>
      </div>

      {toastMessage && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs font-medium text-emerald-900 flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* 9 Work Type Selector Cards */}
      <div className="space-y-2">
        <div className="text-xs font-bold text-slate-700 uppercase tracking-wider">
          Select HERE4U Standard Work Type Template
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-9 gap-2">
          {templates.map((tpl) => {
            const isSelected = tpl.workTypeCode === selectedTemplateCode;
            return (
              <button
                key={tpl.id}
                onClick={() => loadTemplate(tpl)}
                className={`p-2.5 rounded-lg border text-left transition-all text-xs cursor-pointer ${
                  isSelected
                    ? 'border-blue-600 bg-blue-50/80 text-blue-900 ring-2 ring-blue-500/20 shadow-xs'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
                }`}
              >
                <div className="font-mono font-bold text-[10px] text-slate-500">{tpl.workTypeCode}</div>
                <div className="font-bold text-[11px] truncate mt-0.5">{tpl.workTypeName.split(' ')[0]}</div>
                <div className="text-[10px] text-slate-400 truncate mt-0.5">{tpl.defaultUnit}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Estimator Grid: Line Items Editor + Live Financial Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Left Column: Line Items Table & Quantity Inputs */}
        <div className="lg:col-span-8 bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden flex flex-col">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between bg-slate-50/60">
            <div>
              <h2 className="text-sm font-bold text-slate-900">
                {templates.find((t) => t.workTypeCode === selectedTemplateCode)?.workTypeName || 'Work Type'}
              </h2>
              <p className="text-xs text-slate-500 mt-0.5">
                {templates.find((t) => t.workTypeCode === selectedTemplateCode)?.description}
              </p>
            </div>

            <button
              onClick={handleAddItem}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add Item
            </button>
          </div>

          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-slate-600 font-semibold">
                  <th className="py-2.5 px-3 w-8">#</th>
                  <th className="py-2.5 px-3">Description</th>
                  <th className="py-2.5 px-3 w-20">Unit</th>
                  <th className="py-2.5 px-3 w-20">Qty</th>
                  <th className="py-2.5 px-3 w-24">Material (PKR)</th>
                  <th className="py-2.5 px-3 w-24">Labour (PKR)</th>
                  <th className="py-2.5 px-3 w-28 text-right">Line Total</th>
                  <th className="py-2.5 px-3 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700">
                {lineItems.map((item, idx) => {
                  const qty = Number(item.defaultQty) || 0;
                  const lineTotal =
                    qty *
                    (Number(item.materialRate || 0) +
                      Number(item.labourRate || 0) +
                      Number(item.equipmentRate || 0) +
                      Number(item.transportRate || 0));

                  return (
                    <tr key={idx} className="hover:bg-slate-50/60">
                      <td className="py-2.5 px-3 text-slate-400 font-mono text-[11px]">{idx + 1}</td>
                      <td className="py-2.5 px-3">
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleUpdateItem(idx, 'description', e.target.value)}
                          className="w-full px-2 py-1 bg-transparent hover:bg-white focus:bg-white border border-transparent hover:border-slate-200 focus:border-blue-500 rounded text-xs"
                        />
                      </td>
                      <td className="py-2.5 px-3">
                        <input
                          type="text"
                          value={item.unit}
                          onChange={(e) => handleUpdateItem(idx, 'unit', e.target.value)}
                          className="w-16 px-1.5 py-1 bg-transparent hover:bg-white focus:bg-white border border-transparent hover:border-slate-200 focus:border-blue-500 rounded text-xs"
                        />
                      </td>
                      <td className="py-2.5 px-3">
                        <input
                          type="number"
                          step="any"
                          value={item.defaultQty}
                          onChange={(e) => handleUpdateItem(idx, 'defaultQty', parseFloat(e.target.value) || 0)}
                          className="w-16 px-1.5 py-1 bg-slate-50 border border-slate-200 focus:border-blue-500 rounded text-xs font-semibold"
                        />
                      </td>
                      <td className="py-2.5 px-3">
                        <input
                          type="number"
                          value={item.materialRate}
                          onChange={(e) => handleUpdateItem(idx, 'materialRate', parseFloat(e.target.value) || 0)}
                          className="w-20 px-1.5 py-1 bg-slate-50 border border-slate-200 focus:border-blue-500 rounded text-xs font-mono"
                        />
                      </td>
                      <td className="py-2.5 px-3">
                        <input
                          type="number"
                          value={item.labourRate}
                          onChange={(e) => handleUpdateItem(idx, 'labourRate', parseFloat(e.target.value) || 0)}
                          className="w-20 px-1.5 py-1 bg-slate-50 border border-slate-200 focus:border-blue-500 rounded text-xs font-mono"
                        />
                      </td>
                      <td className="py-2.5 px-3 text-right font-mono font-bold text-slate-900">
                        {lineTotal.toLocaleString()}
                      </td>
                      <td className="py-2.5 px-3 text-center">
                        <button
                          onClick={() => handleDeleteItem(idx)}
                          className="text-slate-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right Column: Sliders + Live Breakdown Card */}
        <div className="lg:col-span-4 space-y-4">
          {/* Sliders Card */}
          <div className="p-4 bg-white border border-slate-200 rounded-xl shadow-xs space-y-3.5 text-xs">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Sliders className="w-4 h-4 text-blue-600" />
                <h3 className="font-bold text-slate-900">Commercial Multipliers</h3>
              </div>
              <span
                className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                  isManagerOrAdmin
                    ? 'bg-blue-100 text-blue-800'
                    : 'bg-amber-100 text-amber-900 border border-amber-300'
                }`}
              >
                {isManagerOrAdmin ? 'Manager Control' : 'Locked by Policy'}
              </span>
            </div>

            {!isManagerOrAdmin && (
              <div className="p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-[11px] text-slate-600">
                <strong className="text-slate-800">Estimator Mode:</strong> Quantities &amp; items are editable. Wastage, overheads, margin, and taxes are locked to management template standards.
              </div>
            )}

            {/* Wastage */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-600">Material Wastage Factor</span>
                <span className="font-mono font-bold text-blue-700">{wastagePercent}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="20"
                step="1"
                disabled={!isManagerOrAdmin}
                value={wastagePercent}
                onChange={(e) => setWastagePercent(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Site Overheads */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-600">Site Overheads &amp; Tools</span>
                <span className="font-mono font-bold text-blue-700">{overheadPercent}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="25"
                step="1"
                disabled={!isManagerOrAdmin}
                value={overheadPercent}
                onChange={(e) => setOverheadPercent(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Contractor Margin */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-600">Contractor Profit Margin</span>
                <span className="font-mono font-bold text-blue-700">{markupPercent}%</span>
              </div>
              <input
                type="range"
                min="10"
                max="40"
                step="1"
                disabled={!isManagerOrAdmin}
                value={markupPercent}
                onChange={(e) => setMarkupPercent(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>

            {/* Sales Tax */}
            <div className="space-y-1">
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-slate-600">Sales Tax / STRN</span>
                <span className="font-mono font-bold text-blue-700">{taxPercent}%</span>
              </div>
              <input
                type="range"
                min="0"
                max="20"
                step="1"
                disabled={!isManagerOrAdmin}
                value={taxPercent}
                onChange={(e) => setTaxPercent(Number(e.target.value))}
                className="w-full h-1.5 bg-slate-100 rounded-lg appearance-none cursor-pointer accent-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          {/* Live Financial Breakdown Card */}
          <div className="p-5 bg-gradient-to-br from-slate-900 to-[#0a192f] text-white rounded-xl shadow-md space-y-4">
            <div className="border-b border-slate-700 pb-3 flex items-center justify-between">
              <div>
                <span className="text-[10px] text-blue-400 font-mono uppercase tracking-wider">
                  Cost Estimation Result
                </span>
                <div className="text-xs font-semibold text-slate-300">
                  {templates.find((t) => t.workTypeCode === selectedTemplateCode)?.workTypeName}
                </div>
              </div>
              <Calculator className="w-5 h-5 text-blue-400" />
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex items-center justify-between text-slate-300">
                <span>Material Subtotal:</span>
                <span className="font-mono">PKR {calculation.materialSubtotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-slate-300">
                <span>Labour Subtotal:</span>
                <span className="font-mono">PKR {calculation.labourSubtotal.toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-slate-400 text-[11px]">
                <span>Wastage ({wastagePercent}%):</span>
                <span className="font-mono">PKR {Math.round(calculation.wastageCost).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-slate-400 text-[11px]">
                <span>Overheads ({overheadPercent}%):</span>
                <span className="font-mono">PKR {Math.round(calculation.overheadCost).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-emerald-400 font-medium">
                <span>Contractor Margin ({markupPercent}%):</span>
                <span className="font-mono">+ PKR {Math.round(calculation.markupCost).toLocaleString()}</span>
              </div>
              <div className="flex items-center justify-between text-slate-400 text-[11px]">
                <span>Sales Tax ({taxPercent}%):</span>
                <span className="font-mono">PKR {Math.round(calculation.taxCost).toLocaleString()}</span>
              </div>
            </div>

            <div className="pt-3 border-t border-slate-700 flex items-baseline justify-between">
              <span className="font-bold text-sm text-slate-200">Total Price:</span>
              <div className="text-right">
                <span className="text-xl font-black text-amber-400 font-mono">
                  PKR {Math.round(calculation.grandTotal).toLocaleString()}
                </span>
                <div className="text-[10px] text-slate-400 mt-0.5">Including Taxes &amp; Overheads</div>
              </div>
            </div>

            <button
              onClick={handlePushToBOQ}
              disabled={isPushingBOQ}
              className="w-full py-2.5 text-xs font-bold rounded-lg bg-blue-600 hover:bg-blue-500 text-white transition-colors shadow-sm inline-flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
            >
              <FileSpreadsheet className="w-4 h-4" />
              Generate Official BOQ &rarr;
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
